<template>
  <div v-if="visible" class="mso-card">
    <button class="mso-header" type="button" @click="collapsed = !collapsed">
      <svg class="mso-chevron" :class="{ collapsed }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
      <h3 class="mso-title">Minhas Ordens de Serviço</h3>
      <span class="mso-count">{{ items.length }}</span>
    </button>

    <template v-if="!collapsed">
      <div class="mso-list">
        <div v-for="group in pagedGroups" :key="group.key" class="mso-group" :class="{ 'mso-group--os': group.osTitle }">
          <!-- ── Grupo de OS: colapsado por padrão, 1 único "Completar" que abre a página de preenchimento ── -->
          <template v-if="group.osTitle">
            <button class="mso-os-header" type="button" @click="toggleGroupOpen(group.key)">
              <svg class="mso-chevron" :class="{ collapsed: !openGroups.has(group.key) }" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              <div class="mso-os-heading">
                <span class="mso-os-number">OS{{ group.osNumber }}</span>
                <span class="mso-os-desc">{{ group.osTitle }}</span>
              </div>
              <span class="mso-os-item-count">{{ group.items.length }} {{ group.items.length > 1 ? 'itens' : 'item' }}</span>
              <span class="mso-btn mso-btn-primary mso-btn-complete" @click.stop="goComplete(group)">Completar</span>
            </button>

            <div v-if="openGroups.has(group.key)" class="mso-os-items">
              <div v-for="item in group.items" :key="item.id" class="mso-item">
                <div class="mso-item-title">{{ item.title }}</div>
                <div class="mso-item-meta">
                  {{ item.projectName }}
                  <span v-if="item.dueDate"> · prazo {{ formatDate(item.dueDate) }}</span>
                  <span class="mso-priority-badge" :class="'p-' + item.priority">{{ priorityLabel(item.priority) }}</span>
                </div>
              </div>
            </div>
          </template>

          <!-- ── Item solto (rotina recorrente): mantém navegação direta ── -->
          <template v-else>
            <div v-for="item in group.items" :key="item.id" class="mso-item">
              <div class="mso-item-row">
                <div class="mso-item-main">
                  <div class="mso-item-title">{{ item.title }}</div>
                  <div class="mso-item-meta">
                    {{ item.projectName }}
                    <span v-if="item.dueDate"> · prazo {{ formatDate(item.dueDate) }}</span>
                    <span class="mso-priority-badge" :class="'p-' + item.priority">{{ priorityLabel(item.priority) }}</span>
                  </div>
                </div>
                <button class="mso-btn" type="button" @click="openMaintenance(item)">Concluir</button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div v-if="totalPages > 1" class="mso-pagination">
        <button
          v-for="p in totalPages"
          :key="p"
          class="mso-page-btn"
          :class="{ active: p === currentPage }"
          type="button"
          @click="currentPage = p"
        >{{ p }}</button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { maintenanceStore, type MyServiceOrder } from '@/store/maintenance'
import { systemStore } from '@/store/system'

interface OsGroup {
  key: string
  osId: string
  osTitle: string
  osNumber?: number
  items: MyServiceOrder[]
}

const GROUPS_PER_PAGE = 5

export default defineComponent({
  name: 'MyServiceOrdersCard',
  setup() {
    const router = useRouter()
    const collapsed = ref(false)
    const currentPage = ref(1)
    const openGroups = reactive(new Set<string>())

    const items = computed(() => maintenanceStore.myTasks.items)
    const visible = computed(() => systemStore.isFeatureEnabled('maintenance') && items.value.length > 0)

    const groups = computed<OsGroup[]>(() => {
      const map = new Map<string, OsGroup>()
      for (const item of items.value) {
        const key = item.osId || `__loose-${item.id}`
        let group = map.get(key)
        if (!group) {
          group = { key, osId: item.osId || '', osTitle: item.osId ? item.osTitle : '', osNumber: item.osNumber, items: [] }
          map.set(key, group)
        }
        group.items.push(item)
      }
      return Array.from(map.values())
    })

    const totalPages = computed(() => Math.max(1, Math.ceil(groups.value.length / GROUPS_PER_PAGE)))
    const pagedGroups = computed(() => {
      const start = (currentPage.value - 1) * GROUPS_PER_PAGE
      return groups.value.slice(start, start + GROUPS_PER_PAGE)
    })

    const priorityLabel = (p?: string) => ({ baixa: 'Baixa', normal: 'Normal', alta: 'Alta', urgente: 'Urgente' }[p || 'normal'] || 'Normal')
    const formatDate = (iso: string) => {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      return `${d}/${m}/${y}`
    }

    const openMaintenance = (item: MyServiceOrder) => {
      router.push('/manutencao/' + encodeURIComponent(item.projectId))
    }

    const toggleGroupOpen = (groupKey: string) => {
      if (openGroups.has(groupKey)) openGroups.delete(groupKey)
      else openGroups.add(groupKey)
    }

    const goComplete = (group: OsGroup) => {
      router.push('/preencher-os/' + encodeURIComponent(group.osId))
    }

    onMounted(() => {
      maintenanceStore.fetchMyTasks().catch(() => { /* ignore */ })
    })

    return {
      items, visible, collapsed, currentPage, totalPages, pagedGroups, openGroups,
      priorityLabel, formatDate, openMaintenance, toggleGroupOpen, goComplete
    }
  }
})
</script>

<style scoped lang="scss">
.mso-card {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.mso-header {
  display: flex; align-items: center; gap: 8px;
  background: none; border: none; padding: 0; cursor: pointer; text-align: left; width: 100%;
}
.mso-chevron { color: var(--ink-muted); transition: transform .15s; flex-shrink: 0; &.collapsed { transform: rotate(-90deg); } }
.mso-title { font-size: 15px; font-weight: 700; color: var(--ink); margin: 0; }
.mso-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px;
  background: var(--accent); color: #fff;
  border-radius: 999px; font-size: 11px; font-weight: 700;
}

.mso-list { display: flex; flex-direction: column; gap: 14px; }
.mso-group { display: flex; flex-direction: column; gap: 8px; }

.mso-group--os {
  border: 1.5px solid rgba(31,30,26,.12);
  border-radius: 14px;
  padding: 10px 14px;
  gap: 0;
  background: var(--card, #fff);
}

.mso-os-header {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  background: none; border: none; padding: 4px 0; cursor: pointer; text-align: left; width: 100%;
}
.mso-os-heading { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-width: 0; }
.mso-os-number {
  display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: var(--accent); color: #fff;
  font-size: 13px; font-weight: 800; letter-spacing: .02em;
  padding: 4px 12px; border-radius: 999px;
}
.mso-os-desc { font-size: 16px; font-weight: 700; color: var(--ink); }
.mso-os-item-count { font-size: 12px; color: var(--ink-muted); }

.mso-btn-complete { padding: 10px 22px; font-size: 13px; font-weight: 700; border-radius: 10px; margin-left: auto; }

.mso-os-items { display: flex; flex-direction: column; gap: 8px; padding: 10px 0 6px; }

.mso-item {
  background: var(--bg, #f7f4ee);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mso-item-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }

.mso-item-title { font-size: 13px; font-weight: 700; color: var(--ink); }
.mso-item-meta { font-size: 12px; color: var(--ink-muted); display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }

.mso-priority-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  &.p-baixa { background: rgba(100,116,139,.12); color: #475569; }
  &.p-normal { background: rgba(37,99,235,.12); color: #2563eb; }
  &.p-alta { background: rgba(217,119,6,.12); color: #d97706; }
  &.p-urgente { background: rgba(220,38,38,.12); color: #dc2626; }
}

.mso-btn {
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: var(--accent);
  color: #fff;
  white-space: nowrap;
  display: inline-block;
  &:disabled { opacity: .6; cursor: default; }
}
.mso-btn-primary { background: var(--accent); color: #fff; }

.mso-pagination { display: flex; gap: 6px; flex-wrap: wrap; justify-content: center; }
.mso-page-btn {
  min-width: 28px; height: 28px; padding: 0 6px;
  border: 1px solid rgba(31,30,26,.15); border-radius: 7px;
  background: var(--bg, #fff); color: var(--ink); font-size: 12px; font-weight: 600; cursor: pointer;
  &.active { background: var(--accent); color: #fff; border-color: var(--accent); }
}
</style>
